import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SrId } from "@evergis/react";
import { GeometryType } from "@evergis/api";

import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";

import { DataSchema, ImportAttribute } from "../types";
import { getLayerDefinition } from "../utils/getLayerDefinition";

type CrsOption = {
  value: SrId;
  text: string;
};

export const crsOptions: CrsOption[] = [
  { value: SrId.Wgs84, text: "Lon/Lat WGS84" },
  { value: SrId.WebMercator, text: "Web Mercator WGS84" },
];

/**
 * Наименования полей с координатами и значение географической привязки
 */
export interface SrFields {
  /**
   * название поля со значением долготы
   */
  longitude: string;
  /**
   * название поля со значением широты
   */
  latitude: string;
  /**
   * название поля со значениями долготы и широты
   */
  longitudeAndLatitude: string;
  srid: number;
}

type Attribute = Pick<ImportAttribute, "name">;

type CoordinatesFieldsNames = keyof Omit<SrFields, "srid">;

export type SrFieldUpdateAction = (coordinateType: keyof SrFields, fieldName: string) => void;

export interface SpatialReferenceData {
  srFields: SrFields;
  lonLatInOneField: boolean;
  geometrySpecified?: boolean;
}

export interface SpatialReferenceActions {
  updateSrField: SrFieldUpdateAction;
  setLonLatInOneField: (isInOneField: SpatialReferenceData["lonLatInOneField"]) => void;
}

// eslint-disable-next-line max-lines-per-function
export const useSpatialReference = (
  dataSchema: DataSchema,
  attributes: Attribute[] = [],
): [SpatialReferenceData, SpatialReferenceActions] => {
  const { t } = useTranslation("dialogs");
  const [lonLatInOneField, setLonLatInOneField] = useState(false);
  const [geometrySpecified, setGeometrySpecified] = useState(false);

  const spatialReference = Array.isArray(dataSchema)
    ? getLayerDefinition(dataSchema[0].children).spatialReference
    : (dataSchema as SchemaParsed)?.spatialReference;

  const [srFields, setSrFields] = useState<SrFields>({
    longitude: "",
    latitude: "",
    longitudeAndLatitude: "",
    srid: spatialReference || crsOptions[0].value,
  });

  const coordinatesAttributeNames: Record<keyof Omit<SrFields, "srid">, string[]> = useMemo(
    () => ({
      longitude: ["x", "longitude", "lon", t("import.longitude")],
      latitude: ["y", "latitude", "lat", t("import.latitude")],
      longitudeAndLatitude: [
        "xy",
        "lonlat",
        "coords",
        "coordinates",
        `${t("import.longitude")} ; ${t("import.latitude")}`,
      ],
    }),
    [t],
  );

  const isAttributeSpecific = useCallback(
    (attribute: Attribute, specAttrType: string): boolean => {
      return coordinatesAttributeNames[specAttrType].indexOf(attribute.name.trim().toLowerCase()) > -1;
    },
    [coordinatesAttributeNames],
  );

  // Изменение названия поля, отвечающего за координаты
  const updateSrField = useCallback<SrFieldUpdateAction>(
    (coordinateType, fieldName) => {
      setSrFields({
        ...srFields,
        [coordinateType]: fieldName,
      });
    },
    [srFields],
  );

  // Проверка на наличие наименования атрибута в случае если массив атрибутов был изменен
  const srFieldIsExist = (srFieldName: CoordinatesFieldsNames): boolean =>
    attributes.some(attr => attr.name === srFields[srFieldName]);

  // Автоподстановка конкретного поля
  const initializeSrField = useCallback(
    (srFieldName: CoordinatesFieldsNames) => {
      const { name } = attributes.find(attr => isAttributeSpecific(attr, srFieldName)) || {};

      if (name) {
        updateSrField(srFieldName, name);
      }
    },
    [attributes, isAttributeSpecific, updateSrField],
  );

  // Автоподстановка названий полей связанных с координатами. Словарь - coordinatesAttributeNames
  useEffect(() => {
    if (!Array.isArray(dataSchema)) {
      const { geometryType, geometryAttribute, coordinateFields } = (dataSchema || {}) as SchemaParsed;

      if (!geometrySpecified && geometryType !== GeometryType.Point && geometryAttribute) {
        setGeometrySpecified(true);
        setLonLatInOneField(true);
        updateSrField("longitudeAndLatitude", coordinateFields?.[0] || geometryAttribute);

        return;
      }
    }

    if (lonLatInOneField && !geometrySpecified) {
      if (!srFields.longitudeAndLatitude || !srFieldIsExist("longitudeAndLatitude")) {
        initializeSrField("longitudeAndLatitude");
      }
    } else {
      if (!srFields.longitude || !srFieldIsExist("longitude")) {
        initializeSrField("longitude");
      }
      if (!srFields.latitude || !srFieldIsExist("latitude")) {
        initializeSrField("latitude");
      }
    }
  }, [lonLatInOneField, srFields, srFieldIsExist, initializeSrField, updateSrField, dataSchema, geometrySpecified]);

  return [
    { srFields, lonLatInOneField, geometrySpecified },
    { updateSrField, setLonLatInOneField },
  ];
};
